import { createContext, useContext, useEffect, useRef, useCallback, type ReactNode } from "react";
import { useLocation } from "react-router";

type SRAnnounceOptions = {
    priority?: "polite" | "assertive";
    focusTarget?: HTMLElement | null;
};

type SRAnnouncementContextValue = {
    announce: (message: string, options?: SRAnnounceOptions) => void;
    announceHeading: () => void;
};

const SRAnnouncementContext = createContext<SRAnnouncementContextValue | null>(null);

/**
 * Hook to access screen reader announcement functions.
 * Must be used within SRAnnouncementProvider.
 */
export function useSRAnnouncement(): SRAnnouncementContextValue {
    const context = useContext(SRAnnouncementContext);
    if (!context) {
        throw new Error("useSRAnnouncement must be used within SRAnnouncementProvider");
    }
    return context;
}

type SRAnnouncementProviderProps = {
    children: ReactNode;
};

/**
 * Provider for screen reader announcements.
 * - Automatically announces page heading on route changes
 * - Exposes announce() for programmatic announcements
 * - Exposes announceHeading() to manually trigger heading announcement
 */
export function SRAnnouncementProvider({ children }: SRAnnouncementProviderProps) {
    const location = useLocation();
    const politeAnnouncementRef = useRef<HTMLDivElement>(null);
    const assertiveAnnouncementRef = useRef<HTMLDivElement>(null);
    const isFirstRender = useRef(true);

    /**
     * Announce a message to screen readers.
     * @param message - The message to announce.
     * @param options - The options for the announcement.
     */
    const announce = useCallback((message: string, options?: SRAnnounceOptions) => {
        const priority = options?.priority ?? "polite";
        const ref = priority === "assertive" ? assertiveAnnouncementRef : politeAnnouncementRef;

        if (ref.current) {
            ref.current.textContent = message;
        }

        if (options?.focusTarget) {
            if (!options.focusTarget.hasAttribute("tabindex")) {
                options.focusTarget.setAttribute("tabindex", "-1");
            }
            options.focusTarget.focus({ preventScroll: true });
        }
    }, []);

    /**
     * Announce the heading text to screen readers by focusing the heading.
     * @param selector - The selector to use to find the heading. Defaults to "main h1".
     */
    const announceHeading = useCallback((selector = "main h1") => {
        const heading = document.querySelector(selector) as HTMLHeadingElement | null;

        if (heading) {
            // Ensure heading can receive programmatic focus
            if (!heading.hasAttribute("tabindex")) {
                heading.setAttribute("tabindex", "-1");
            }

            // Focus the heading, which will trigger a screen reader to announce the heading text
            heading.focus({ preventScroll: true });
        }
    }, []);

    // Announce the heading text to screen readers when the route changes.
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const timeoutId = setTimeout(() => {
            // Check if a form input currently has focus (e.g., from React autoFocus)
            const activeElement = document.activeElement;
            const isFormInputFocused =
                activeElement instanceof HTMLInputElement ||
                activeElement instanceof HTMLTextAreaElement ||
                activeElement instanceof HTMLSelectElement;

            if (isFormInputFocused) {
                // Announce via aria-live region without changing focus
                const heading = document.querySelector("main h1") as HTMLHeadingElement | null;
                if (heading?.textContent) {
                    announce(heading.textContent);
                }
            } else {
                // Default behavior: focus the heading
                announceHeading();
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [location.pathname, announce, announceHeading]);

    const contextValue: SRAnnouncementContextValue = {
        announce,
        announceHeading,
    };

    return (
        <SRAnnouncementContext.Provider value={contextValue}>
            {children}
            <div ref={politeAnnouncementRef} aria-live="polite" aria-atomic="true" className="sr-only" />
            <div ref={assertiveAnnouncementRef} aria-live="assertive" aria-atomic="true" className="sr-only" />
        </SRAnnouncementContext.Provider>
    );
}

export default SRAnnouncementProvider;
